/* Golf club distance calculator (sections/club-distance-calculator.liquid).
   Model: driver carry from swing speed x strike coefficient, per-club carry
   ratios interpolated between an amateur set (Shot Scope 15 hcp on-course)
   and a tour set (TrackMan PGA carry), loft profile on irons, roll by club
   category and fairway firmness. Live-updates on every input. */
(function () {
  'use strict';

  var YD_PER_M = 1.09361;
  var MPH_PER_KMH = 0.621371;

  var STRIKE = {
    inconsistent: { k: 2.15, range: 0.15, label: 'Inconsistent' },
    average: { k: 2.3, range: 0.1, label: 'Average' },
    solid: { k: 2.45, range: 0.06, label: 'Solid' },
    flush: { k: 2.55, range: 0.03, label: 'Flush' }
  };
  var LOFT = { traditional: 0.96, standard: 1.0, strong: 1.04 };
  var FAIRWAY = { soft: 0.4, normal: 1.0, firm: 1.6 };

  // id, label, category, amateur ratio, tour ratio, roll % of carry (normal)
  var CLUBS = [
    ['driver', 'Driver', 'driver', 1.0, 1.0, 0.06],
    ['3w', '3-wood', 'wood', 0.9, 0.884, 0.05],
    ['5w', '5-wood', 'wood', 0.84, 0.836, 0.05],
    ['3h', '3-hybrid', 'hybrid', 0.81, 0.818, 0.04],
    ['4h', '4-hybrid', 'hybrid', 0.78, 0.78, 0.04],
    ['4i', '4-iron', 'iron', 0.76, 0.738, 0.04],
    ['5i', '5-iron', 'iron', 0.72, 0.705, 0.04],
    ['6i', '6-iron', 'iron', 0.69, 0.665, 0.03],
    ['7i', '7-iron', 'iron', 0.65, 0.625, 0.03],
    ['8i', '8-iron', 'iron', 0.615, 0.582, 0.025],
    ['9i', '9-iron', 'iron', 0.575, 0.538, 0.025],
    ['pw', 'Pitching wedge', 'iron', 0.51, 0.495, 0.02],
    ['gw', 'Gap wedge', 'wedge', 0.44, 0.44, 0.02],
    ['sw', 'Sand wedge', 'wedge', 0.36, 0.38, 0.01],
    ['lw', 'Lob wedge', 'wedge', 0.32, 0.31, 0.01]
  ];

  // Benchmarks. Driver = total distance (Arccos 2026 Driving Distance Report,
  // 2025 season). 7-iron = on-course total (Shot Scope 2026). est: true marks
  // a band interpolated between published bands.
  var BENCH = {
    men: {
      handicap: {
        '0-4': { driver: 244, seven: 178 },
        '5-9': { driver: 234, seven: 162 },
        '10-14': { driver: 223, seven: 161 },
        '15-19': { driver: 212, seven: 154 },
        '20-24': { driver: 199, seven: 146 },
        '25-29': { driver: 194, seven: 132 },
        '30+': { driver: 181, seven: 124, est: true }
      },
      age: {
        '20s': 237, '30s': 235, '40s': 230, '50s': 219, '60s': 206, '70s': 190
      },
      all: 224
    },
    women: {
      handicap: {
        '0-4': { driver: 220, seven: 156 },
        '5-9': { driver: 206, seven: 147, est: true },
        '10-14': { driver: 195, seven: 133, est: true },
        '15-19': { driver: 178, seven: 119 },
        '20-24': { driver: 167, seven: 107 },
        '25-29': { driver: 154, seven: 103 },
        '30+': { driver: 145, seven: 95, est: true }
      },
      age: {
        '20s': 201, '30s': 195, '40s': 187, '50s': 175, '60s': 158, '70s': 145
      },
      ageEst: { '30s': true, '40s': true, '50s': true, '70s': true },
      all: 176
    }
  };
  // PGA: 2026 ShotLink driving distance and the scaled TrackMan 7-iron; LPGA: 2025 driving distance, TrackMan 2024 7-iron.
  var TOUR = { pga: { driver: 306, seven: 193, speed: 117 }, lpga: { driver: 256, seven: 155, speed: 96 } };

  function clamp(n, lo, hi) { return Math.max(lo, Math.min(hi, n)); }

  function ratioFor(club, driverCarry) {
    var w = clamp((driverCarry - 200) / 75, 0, 1);
    return club[3] * (1 - w) + club[4] * w;
  }

  function sevenIronRatio(driverCarry, loft) {
    var seven = CLUBS[8];
    return ratioFor(seven, driverCarry) * LOFT[loft];
  }

  // Everything internal is yards and mph.
  function compute(state) {
    var strike = STRIKE[state.strike];
    var driverCarry;
    if (state.anchor === 'speed') {
      driverCarry = state.speed * strike.k;
    } else if (state.anchor === 'driver') {
      driverCarry = state.driverCarry;
    } else {
      // 7-iron anchor: ratio depends on driver carry, so iterate.
      driverCarry = state.sevenCarry / 0.64;
      for (var i = 0; i < 5; i++) {
        driverCarry = state.sevenCarry / sevenIronRatio(driverCarry, state.loft);
      }
    }
    driverCarry = clamp(driverCarry, 90, 380);
    var speed = driverCarry / strike.k;
    var rows = CLUBS.map(function (club) {
      var carry = driverCarry * ratioFor(club, driverCarry);
      if (club[2] === 'iron') carry *= LOFT[state.loft];
      var roll = carry * club[5] * FAIRWAY[state.fairway];
      return {
        id: club[0], label: club[1], cat: club[2],
        carry: carry, total: carry + roll,
        lo: carry * (1 - strike.range), hi: carry * (1 + strike.range)
      };
    });
    rows.forEach(function (r, i) {
      var next = rows[i + 1];
      r.gap = next ? r.carry - next.carry : null;
    });
    var irons = rows.filter(function (r) { return r.cat === 'iron'; });
    var ironGap = 0;
    for (var j = 0; j < irons.length - 1; j++) ironGap += irons[j].carry - irons[j + 1].carry;
    ironGap = ironGap / (irons.length - 1);
    return {
      speed: speed, ballSpeed: speed * 1.48,
      sevenSpeed: speed * 0.8,
      driverCarry: driverCarry,
      rows: rows, ironGap: ironGap,
      strike: strike
    };
  }

  function toUnit(yards, metric) { return metric ? yards / YD_PER_M : yards; }
  function fmt(yards, metric) { return String(Math.round(toUnit(yards, metric))); }
  function unitLabel(metric) { return metric ? 'm' : 'yds'; }

  function init(root) {
    var metric = false;
    var state = {
      anchor: 'speed', speed: 93, driverCarry: 214, sevenCarry: 140,
      strike: 'solid', loft: 'standard', fairway: 'normal',
      gender: 'men', handicap: '', age: ''
    };
    try {
      var saved = JSON.parse(window.localStorage.getItem('wg-club-distance') || 'null');
      if (saved && typeof saved === 'object') {
        Object.keys(state).forEach(function (k) { if (saved[k] !== undefined) state[k] = saved[k]; });
        if (saved.metric) metric = true;
      }
      state.strike = 'solid'; // the strike selector was removed; the model assumes a solid strike
    } catch (e) { /* storage unavailable */ }

    var q = function (sel) { return root.querySelector(sel); };
    var qa = function (sel) { return Array.prototype.slice.call(root.querySelectorAll(sel)); };

    var numberInput = q('[data-cdc-number]');
    var numberUnit = q('[data-cdc-number-unit]');
    var numberLabel = q('[data-cdc-number-label]');
    var numberHint = q('[data-cdc-number-hint]');
    var range = q('[data-cdc-range]');
    var rangeLo = q('[data-cdc-range-lo]');
    var rangeHi = q('[data-cdc-range-hi]');
    var results = q('[data-cdc-results]');
    var tbody = q('[data-cdc-tbody]');
    var copyBtn = q('[data-cdc-copy]');
    var copied = q('[data-cdc-copied]');
    var compare = q('[data-cdc-compare]');

    var ANCHORS = {
      speed: { label: 'Driver swing speed', unit: function () { return metric ? 'km/h' : 'mph'; }, min: 55, max: 140, step: 1, hint: 'PGA Tour average 117 mph (2026 season). Average male amateur 93, female 79.' },
      driver: { label: 'Driver carry distance', unit: function () { return unitLabel(metric); }, min: 110, max: 340, step: 1, hint: 'Carry, not total. Use a launch monitor or GPS number from a flushed drive.' },
      seven: { label: '7-iron carry distance', unit: function () { return unitLabel(metric); }, min: 70, max: 220, step: 1, hint: 'Your stock, well-struck 7-iron. Average male amateur 140 to 150, female 100 to 110.' }
    };

    function currentValueDisplay() {
      var a = state.anchor;
      var v = a === 'speed' ? state.speed : a === 'driver' ? state.driverCarry : state.sevenCarry;
      if (a === 'speed') return metric ? v / MPH_PER_KMH : v;
      return toUnit(v, metric);
    }

    function setValueFromDisplay(display) {
      var a = state.anchor;
      var v = Number(display);
      if (!isFinite(v)) return;
      if (a === 'speed') state.speed = clamp(metric ? v * MPH_PER_KMH : v, 40, 160);
      else if (a === 'driver') state.driverCarry = clamp(metric ? v * YD_PER_M : v, 90, 380);
      else state.sevenCarry = clamp(metric ? v * YD_PER_M : v, 50, 240);
    }

    function syncInputs() {
      var a = ANCHORS[state.anchor];
      numberLabel.textContent = a.label;
      numberUnit.textContent = a.unit();
      numberHint.textContent = a.hint;
      var conv = state.anchor === 'speed' ? (metric ? 1 / MPH_PER_KMH : 1) : (metric ? 1 / YD_PER_M : 1);
      range.min = Math.round(a.min * conv);
      range.max = Math.round(a.max * conv);
      range.step = a.step;
      rangeLo.textContent = range.min + ' ' + a.unit();
      rangeHi.textContent = range.max + ' ' + a.unit();
      var display = Math.round(currentValueDisplay());
      numberInput.value = display;
      range.value = display;
      qa('[data-cdc-seg]').forEach(function (group) {
        var key = group.getAttribute('data-cdc-seg');
        qa('[data-cdc-seg="' + key + '"] button').forEach(function (b) {
          b.setAttribute('aria-pressed', String(b.getAttribute('data-value') === String(state[key])));
        });
      });
      qa('[data-cdc-units] button').forEach(function (b) {
        b.setAttribute('aria-pressed', String((b.getAttribute('data-value') === 'metric') === metric));
      });
      qa('[data-cdc-unit-text]').forEach(function (el) { el.textContent = unitLabel(metric); });
    }

    function deltaHtml(mine, bench, est) {
      var d = Math.round(toUnit(mine - bench, metric));
      var cls = d >= 0 ? 'cdc__delta--up' : 'cdc__delta--down';
      var sign = d > 0 ? '+' : '';
      return '<span class="cdc__delta ' + cls + '">' + sign + d + ' ' + unitLabel(metric) + (est ? ' (band interpolated)' : '') + '</span>';
    }

    function renderCompare(r) {
      if (!compare) return;
      var driverTotal = r.rows[0].total;
      var sevenTotal = r.rows[8].total;
      var g = BENCH[state.gender] || BENCH.men;
      var who = state.gender === 'women' ? 'women' : 'men';
      var lines = [];
      lines.push('<li>All ' + who + ' (Arccos, ' + fmt(g.all, metric) + ' ' + unitLabel(metric) + ' driver total): ' + deltaHtml(driverTotal, g.all) + '</li>');
      if (state.handicap && g.handicap[state.handicap]) {
        var hb = g.handicap[state.handicap];
        lines.push('<li>' + state.handicap + ' handicap driver (' + fmt(hb.driver, metric) + '): ' + deltaHtml(driverTotal, hb.driver) + '</li>');
        lines.push('<li>' + state.handicap + ' handicap 7-iron (' + fmt(hb.seven, metric) + '): ' + deltaHtml(sevenTotal, hb.seven, hb.est) + '</li>');
      }
      if (state.age && g.age[state.age]) {
        var est = g.ageEst && g.ageEst[state.age];
        lines.push('<li>' + who.charAt(0).toUpperCase() + who.slice(1) + ' in their ' + state.age + ' driver (' + fmt(g.age[state.age], metric) + '): ' + deltaHtml(driverTotal, g.age[state.age], est) + '</li>');
      }
      var tour = state.gender === 'women' ? TOUR.lpga : TOUR.pga;
      var tourName = state.gender === 'women' ? 'LPGA Tour' : 'PGA Tour';
      lines.push('<li>' + tourName + ' driver (' + fmt(tour.driver, metric) + ') ' + deltaHtml(driverTotal, tour.driver) + ', 7-iron (' + fmt(tour.seven, metric) + ') ' + deltaHtml(sevenTotal, tour.seven) + '</li>');
      compare.innerHTML = lines.join('');
    }

    function render() {
      var r = compute(state);
      var u = unitLabel(metric);
      q('[data-cdc-stat="driver"]').textContent = fmt(r.rows[0].total, metric);
      q('[data-cdc-stat="seven"]').textContent = fmt(r.rows[8].carry, metric);
      q('[data-cdc-stat="speed"]').textContent = String(Math.round(metric ? r.speed / MPH_PER_KMH : r.speed));
      qa('[data-cdc-stat^="speed-unit"]').forEach(function (el) { el.textContent = metric ? 'km/h' : 'mph'; });
      q('[data-cdc-stat="gap"]').textContent = fmt(r.ironGap, metric);
      q('[data-cdc-stat="ball"]').textContent = String(Math.round(metric ? r.ballSpeed / MPH_PER_KMH : r.ballSpeed));
      q('[data-cdc-stat="carry"]').textContent = fmt(r.driverCarry, metric);

      var maxCarry = r.rows[0].total;
      var html = r.rows.map(function (row, i) {
        var gapCell = '';
        if (row.gap !== null) {
          var g = Math.round(toUnit(row.gap, metric));
          var gy = Math.round(row.gap);
          var flag = '';
          // Flags only from the 5-iron down: the top of the bag lists every
          // wood, hybrid and long iron, and nobody carries all of them.
          if (i >= 6) {
            if (gy < 8) flag = ' <span class="cdc__delta cdc__delta--down" title="Less than 8 yards to the next club">stacked</span>';
            else if (gy > 18) flag = ' <span class="cdc__delta cdc__delta--down" title="More than 18 yards to the next club">hole</span>';
          }
          gapCell = g + flag;
        } else {
          gapCell = '<span class="cdc__delta">shortest</span>';
        }
        var pct = Math.max(4, Math.round((row.total / maxCarry) * 100));
        return '<tr' + (row.id === '7i' ? ' class="cdc__row--seven"' : '') + '>' +
          '<th scope="row">' + row.label + (row.id === '7i' ? ' <span class="cdc__delta">scoring club</span>' : '') + '</th>' +
          '<td class="cdc--num">' + fmt(row.carry, metric) + '</td>' +
          '<td class="cdc--num cdc--total">' + fmt(row.total, metric) + '</td>' +
          '<td class="cdc--num"><span class="cdc__delta">' + fmt(row.lo, metric) + ' to ' + fmt(row.hi, metric) + '</span></td>' +
          '<td class="cdc--num">' + gapCell + '</td>' +
          '<td class="cdc__bar-cell"><span class="cdc__bar" style="width:' + pct + '%"></span></td>' +
          '</tr>';
      }).join('');
      tbody.innerHTML = html;
      renderCompare(r);
      results.removeAttribute('hidden');

      try {
        var save = {}; Object.keys(state).forEach(function (k) { save[k] = state[k]; }); save.metric = metric;
        window.localStorage.setItem('wg-club-distance', JSON.stringify(save));
      } catch (e) { /* storage unavailable */ }
      return r;
    }

    function yardageCardText() {
      var r = compute(state);
      var u = unitLabel(metric);
      var lines = ['WhyGolf yardage card (' + u + ', carry / total)'];
      lines.push('Driver swing speed ~' + Math.round(metric ? r.speed / MPH_PER_KMH : r.speed) + (metric ? ' km/h' : ' mph') + ', ' + r.strike.label.toLowerCase() + ' strike');
      r.rows.forEach(function (row) {
        lines.push(row.label + ': ' + fmt(row.carry, metric) + ' / ' + fmt(row.total, metric));
      });
      lines.push('Estimates from whygolf.com/pages/golf-club-distance-calculator. Track real shots in the WhyGolf App.');
      return lines.join('\n');
    }

    // Wiring
    qa('[data-cdc-seg] button').forEach(function (b) {
      b.addEventListener('click', function () {
        var key = b.parentNode.getAttribute('data-cdc-seg');
        var val = b.getAttribute('data-value');
        state[key] = (state[key] === val && (key === 'handicap' || key === 'age')) ? '' : val;
        syncInputs();
        render();
      });
    });
    qa('[data-cdc-units] button').forEach(function (b) {
      b.addEventListener('click', function () {
        metric = b.getAttribute('data-value') === 'metric';
        syncInputs();
        render();
      });
    });
    numberInput.addEventListener('input', function () {
      if (numberInput.value === '') return;
      setValueFromDisplay(numberInput.value);
      range.value = numberInput.value;
      render();
    });
    numberInput.addEventListener('change', function () { syncInputs(); render(); });
    range.addEventListener('input', function () {
      setValueFromDisplay(range.value);
      numberInput.value = range.value;
      render();
    });
    if (copyBtn) {
      copyBtn.addEventListener('click', function () {
        var text = yardageCardText();
        var done = function () {
          if (copied) { copied.hidden = false; setTimeout(function () { copied.hidden = true; }, 2400); }
        };
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(text).then(done, function () { window.prompt('Copy your yardage card', text); });
        } else {
          window.prompt('Copy your yardage card', text);
        }
      });
    }
    syncInputs();
    render();
  }

  // Chart tabs live in the content section (sections/club-distance-content.liquid).
  function wireTabs(root) {
    var qa = function (sel) { return Array.prototype.slice.call(root.querySelectorAll(sel)); };
    qa('[data-cdc-tab]').forEach(function (tab) {
      tab.addEventListener('click', function () {
        var group = tab.getAttribute('data-cdc-tab');
        var target = tab.getAttribute('data-target');
        qa('[data-cdc-tab="' + group + '"]').forEach(function (t) { t.setAttribute('aria-selected', String(t === tab)); });
        qa('[data-cdc-panel="' + group + '"]').forEach(function (p) { p.hidden = p.getAttribute('data-id') !== target; });
      });
    });
  }

  function boot() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-club-distance-calculator]'), function (root) {
      if (!root.hasAttribute('data-cdc-ready')) { root.setAttribute('data-cdc-ready', ''); init(root); }
    });
    Array.prototype.forEach.call(document.querySelectorAll('[data-club-distance-content]'), function (root) {
      if (!root.hasAttribute('data-cdc-ready')) { root.setAttribute('data-cdc-ready', ''); wireTabs(root); }
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', boot);
})();
